import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Pencil, PlusCircle, Trash2, Zap } from "lucide-react";
import { toast } from "sonner";
import { useInvoice, useDeleteInvoice } from "@/hooks/useInvoices";
import { InvoiceStatusBadge } from "@/components/ui/invoice-status-badge";
import { Badge } from "@/components/ui/badge";
import { isQuickReceiptInvoice } from "@/lib/invoice-source";
import { Button } from "@/components/ui/button";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { PaymentModal } from "./PaymentModal";

const PAYMENT_METHOD_LABEL: Record<string, string> = {
  PIX: "PIX",
  BANK_TRANSFER: "Transferência",
  BANK_SLIP: "Boleto",
  CASH: "Dinheiro",
  OTHER: "Outro",
};

function formatMoney(value: string | number | null) {
  if (value === null || value === undefined) return "—";
  const n = typeof value === "string" ? Number.parseFloat(value) : value;
  if (Number.isNaN(n)) return "—";
  return new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(n);
}

function formatDate(iso: string | null | undefined) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("pt-BR");
}

function DetailRow({ label, value }: { label: string; value?: string | null }) {
  return (
    <div>
      <dt className="text-xs font-medium text-muted-foreground">{label}</dt>
      <dd className="mt-0.5 text-sm text-foreground">{value || "—"}</dd>
    </div>
  );
}

export function InvoiceDetailPage() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { data: invoice, isLoading } = useInvoice(id ?? "");
  const { mutate: deleteInvoice, isPending: isDeleting } = useDeleteInvoice();
  const [paymentOpen, setPaymentOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);

  if (isLoading) {
    return (
      <div className="flex h-32 items-center justify-center">
        <p className="text-sm text-muted-foreground">Carregando…</p>
      </div>
    );
  }

  if (!invoice) {
    return (
      <div className="flex h-32 items-center justify-center">
        <p className="text-sm text-muted-foreground">Fatura não encontrada.</p>
      </div>
    );
  }

  const total = Number.parseFloat(invoice.totalAmount);
  const paid = invoice.payments.reduce((sum, p) => sum + Number.parseFloat(p.amount), 0);
  const remaining = Math.max(0, total - paid);
  const isPaid = invoice.status === "PAID";
  const canEdit = invoice.status === "PENDING" && !invoice.isOverdue;

  function handleDelete() {
    deleteInvoice(invoice!.id, {
      onSuccess: () => {
        toast.success("Fatura excluída.");
        navigate("/invoices");
      },
      onError: () => toast.error("Erro ao excluir fatura."),
    });
  }

  return (
    <div className="mx-auto max-w-2xl space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-start gap-3">
        <Button variant="ghost" size="icon-sm" onClick={() => navigate("/invoices")}>
          <ArrowLeft size={16} />
        </Button>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <h1 className="text-xl font-semibold text-foreground">
              Fatura {invoice.invoiceNumber}
            </h1>
            <InvoiceStatusBadge status={invoice.status} isOverdue={invoice.isOverdue} />
            {isQuickReceiptInvoice(invoice.invoiceNumber) && (
              <Badge tone="info" icon={<Zap size={11} />}>via atalho</Badge>
            )}
          </div>
          <p className="text-sm text-muted-foreground truncate">{invoice.company.tradeName}</p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {canEdit && (
            <>
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate(`/invoices/${invoice.id}/edit`)}
              >
                <Pencil size={14} />
                Editar
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setDeleteOpen(true)}
              >
                <Trash2 size={14} />
              </Button>
            </>
          )}
          {!isPaid && (
            <Button size="sm" onClick={() => setPaymentOpen(true)}>
              <PlusCircle size={14} />
              Registrar pagamento
            </Button>
          )}
        </div>
      </div>

      <div className="space-y-5 rounded-lg border border-border p-5">
        {/* Identificação */}
        <div>
          <h2 className="mb-3 text-sm font-medium text-foreground">Identificação</h2>
          <dl className="grid grid-cols-2 gap-4 sm:grid-cols-3">
            <DetailRow label="Nº da fatura" value={invoice.invoiceNumber} />
            <DetailRow label="Nº da NF" value={invoice.nfNumber} />
            <DetailRow label="Data da NF" value={formatDate(invoice.nfDate)} />
            <DetailRow label="Emissão" value={formatDate(invoice.issueDate)} />
            <DetailRow
              label="Vencimento"
              value={formatDate(invoice.dueDate)}
            />
          </dl>
        </div>

        {/* Financeiro */}
        <div className="border-t border-border pt-4">
          <h2 className="mb-3 text-sm font-medium text-foreground">Financeiro</h2>
          <dl className="grid grid-cols-2 gap-4 sm:grid-cols-3">
            <DetailRow label="Total da fatura" value={formatMoney(invoice.totalAmount)} />
            <DetailRow label="Pago" value={formatMoney(paid)} />
            <div>
              <dt className="text-xs font-medium text-muted-foreground">Saldo devedor</dt>
              <dd className={`mt-0.5 text-sm font-semibold ${remaining > 0 ? "text-orange-600" : "text-green-700"}`}>
                {remaining > 0 ? formatMoney(remaining) : "Quitado"}
              </dd>
            </div>
          </dl>
        </div>

        {/* Fretes */}
        {invoice.freights.length > 0 && (
          <div className="border-t border-border pt-4">
            <h2 className="mb-3 text-sm font-medium text-foreground">
              Fretes cobertos ({invoice.freights.length})
            </h2>
            <div className="divide-y divide-border">
              {invoice.freights.map((freight) => (
                <div
                  key={freight.id}
                  className="flex items-center justify-between py-2 cursor-pointer hover:bg-muted/30 rounded px-1 transition-colors"
                  onClick={() => navigate(`/freights/${freight.id}`)}
                  onKeyDown={(e) => e.key === "Enter" && navigate(`/freights/${freight.id}`)}
                  role="link"
                  tabIndex={0}
                >
                  <span className="text-sm text-foreground">
                    {freight.originCity}/{freight.originState} →{" "}
                    {freight.destinationCity}/{freight.destinationState}
                  </span>
                  <span className="text-sm font-medium text-foreground">
                    {formatMoney(freight.freightValue)}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Pagamentos */}
        <div className="border-t border-border pt-4">
          <h2 className="mb-3 text-sm font-medium text-foreground">
            Pagamentos recebidos{invoice.payments.length > 0 ? ` (${invoice.payments.length})` : ""}
          </h2>
          {invoice.payments.length === 0 ? (
            <p className="text-sm text-muted-foreground italic">Nenhum pagamento registrado.</p>
          ) : (
            <div className="divide-y divide-border">
              {invoice.payments.map((payment) => (
                <div key={payment.id} className="flex items-center justify-between py-2.5">
                  <div>
                    <p className="text-sm text-foreground">{formatDate(payment.paymentDate)}</p>
                    <p className="text-xs text-muted-foreground">
                      {PAYMENT_METHOD_LABEL[payment.method] ?? payment.method}
                      {payment.notes ? ` — ${payment.notes}` : ""}
                    </p>
                  </div>
                  <span className="text-sm font-medium text-green-700">
                    {formatMoney(payment.amount)}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <PaymentModal
        invoiceId={invoice.id}
        remainingAmount={remaining}
        open={paymentOpen}
        onClose={() => setPaymentOpen(false)}
        onSuccess={() => toast.success("Pagamento registrado!")}
      />

      <ConfirmDialog
        open={deleteOpen}
        title="Excluir fatura"
        description={`Deseja excluir a fatura ${invoice.invoiceNumber}? Esta ação não pode ser desfeita.`}
        confirmLabel="Excluir"
        onConfirm={handleDelete}
        onCancel={() => setDeleteOpen(false)}
        loading={isDeleting}
        destructive
      />
    </div>
  );
}
